import React, { useEffect, useMemo, useState } from 'react';
import {
  MapPin,
  Navigation,
  Truck,
  User,
  Phone,
  Clock,
  Package,
  Activity
} from 'lucide-react';
import io from 'socket.io-client';
import { apiClient, API_BASE_URL, endpoints } from '../../api';
import { getApiKey } from '../../utils/auth';
import './AdminDeliveryTracking.css';

const formatDateTime = (value) => {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

const AdminDeliveryTracking = () => {
  const [trackingItems, setTrackingItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [socket, setSocket] = useState(null);

  const adminKey = useMemo(() => getApiKey(), []);
  const adminToken = useMemo(
    () => localStorage.getItem('adminToken') || localStorage.getItem('token') || '',
    []
  );

  const fetchLiveTracking = async () => {
    try {
      setLoading(true);
      setError('');
      const { data } = await apiClient.get(endpoints.adminDeliveryLiveTracking, {
        headers: {
          ...(adminToken ? { Authorization: `Bearer ${adminToken}` } : {}),
          ...(adminKey ? { 'x-admin-key': adminKey } : {})
        }
      });
      setTrackingItems(Array.isArray(data?.tracking) ? data.tracking : []);
    } catch (e) {
      setError('Failed to load live tracking data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLiveTracking();
  }, []);

  // Socket for live tracking updates
  useEffect(() => {
    const base = API_BASE_URL || window.location.origin;
    const s = io(base, {
      auth: { token: adminKey || adminToken || getApiKey(), type: 'admin' }
    });

    s.on('connect', () => {
      s.emit('join_room', 'admin');
    });

    s.on('delivery_tracking_update', (data) => {
      if (!data) return;
      setTrackingItems((prev) => {
        const items = Array.isArray(prev) ? [...prev] : [];
        const idx = items.findIndex(
          (it) => it.delivery_order_id === data.delivery_order_id
        );
        const next = {
          ...(idx >= 0 ? items[idx] : {}),
          ...data
        };
        if (idx >= 0) {
          items[idx] = next;
        } else {
          items.unshift(next);
        }
        return items;
      });
    });

    setSocket(s);
    return () => {
      s.disconnect();
    };
  }, [adminKey, adminToken]);

  const activeItems = trackingItems || [];
  const selectedItem =
    selectedOrderId != null
      ? activeItems.find((it) => it.delivery_order_id === selectedOrderId)
      : activeItems[0] || null;

  // Build a Google Maps link for the selected partner/order, if coordinates are available
  const mapsLink =
    selectedItem && selectedItem.current_latitude && selectedItem.current_longitude
      ? `https://www.google.com/maps?q=${selectedItem.current_latitude},${selectedItem.current_longitude}`
      : null;

  return (
    <div className="admin-delivery-tracking">
      <div className="tracking-header">
        <div>
          <h1>
            <Navigation size={28} style={{ marginRight: 8 }} />
            Delivery Tracking
          </h1>
          <p>Live map of active delivery partners and in-progress orders.</p>
        </div>
        <div className="tracking-header-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={fetchLiveTracking}
            disabled={loading}
          >
            <Clock size={18} />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="tracking-error">
          {error}
        </div>
      )}

      <div className="tracking-layout">
        {/* Left: Orders / partners list */}
        <div className="tracking-list-panel">
          <div className="tracking-list-header">
            <h2>Active Deliveries</h2>
            <span className="badge">{activeItems.length}</span>
          </div>
          <div className="tracking-list">
            {loading ? (
              <div className="tracking-list-empty">Loading live tracking…</div>
            ) : activeItems.length === 0 ? (
              <div className="tracking-list-empty">
                <Truck size={32} />
                <h3>No active deliveries</h3>
                <p>Assign orders to delivery partners to see them here.</p>
              </div>
            ) : (
              activeItems.map((item) => (
                <button
                  key={`${item.delivery_order_id}-${item.partner_id}`}
                  className={`tracking-list-item ${
                    selectedItem && selectedItem.delivery_order_id === item.delivery_order_id
                      ? 'active'
                      : ''
                  }`}
                  type="button"
                  onClick={() => setSelectedOrderId(item.delivery_order_id)}
                >
                  <div className="tracking-item-main">
                    <div className="tracking-item-title">
                      <span className="code">{item.delivery_code || item.order_number}</span>
                      <span className={`status status-${item.delivery_status || ''}`}>
                        {item.delivery_status}
                      </span>
                    </div>
                    <div className="tracking-item-sub">
                      <span>
                        <User size={14} /> {item.full_name}
                      </span>
                      <span>
                        <Phone size={14} /> {item.customer_phone}
                      </span>
                    </div>
                  </div>
                  <div className="tracking-item-meta">
                    <div className="tracking-item-meta-row">
                      <MapPin size={14} />
                      <span>{item.current_address || item.delivery_address}</span>
                    </div>
                    <div className="tracking-item-meta-row">
                      <Clock size={14} />
                      <span>Last update: {formatDateTime(item.last_location_update)}</span>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Right: "Map" + details */}
        <div className="tracking-map-panel">
          <div className="tracking-map-container">
            {selectedItem && mapsLink ? (
              <div className="tracking-map-fallback">
                <div className="tracking-map-fallback-info">
                  <h3>
                    <Navigation size={18} style={{ marginRight: 6 }} />
                    Live Location Preview
                  </h3>
                  <p>
                    Partner <strong>{selectedItem.full_name}</strong> is currently near:
                  </p>
                  <p className="tracking-address">
                    {selectedItem.current_address || 'Address not available'}
                  </p>
                  <p className="tracking-updated">
                    Last update: {formatDateTime(selectedItem.last_location_update)}
                  </p>
                  <a
                    href={mapsLink}
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-primary btn-sm"
                  >
                    <MapPin size={14} />
                    Open in Google Maps
                  </a>
                </div>
              </div>
            ) : (
              <div className="tracking-map-fallback tracking-map-empty">
                <Navigation size={32} />
                <h3>No live location selected</h3>
                <p>Select an active delivery from the list to view its current location.</p>
              </div>
            )}
          </div>

          {/* Order details / status timeline placeholder */}
          <div className="tracking-details-panel">
            {selectedItem ? (
              <>
                <div className="tracking-details-header">
                  <h2>
                    <Package size={18} style={{ marginRight: 6 }} />
                    {selectedItem.delivery_code || selectedItem.order_number}
                  </h2>
                  <span className={`status status-${selectedItem.delivery_status || ''}`}>
                    {selectedItem.delivery_status}
                  </span>
                </div>
                <div className="tracking-details-grid">
                  <div>
                    <h4>Delivery Partner</h4>
                    <p>{selectedItem.full_name} ({selectedItem.partner_code})</p>
                    <p>{selectedItem.current_address || 'Location not available'}</p>
                  </div>
                  <div>
                    <h4>Customer</h4>
                    <p>{selectedItem.customer_name}</p>
                    <p>{selectedItem.customer_phone}</p>
                  </div>
                  <div>
                    <h4>Pickup</h4>
                    <p>{selectedItem.pickup_address}</p>
                  </div>
                  <div>
                    <h4>Destination</h4>
                    <p>{selectedItem.delivery_address}</p>
                  </div>
                </div>

                <div className="tracking-timeline">
                  <div className="timeline-header">
                    <h3>
                      <Activity size={16} style={{ marginRight: 6 }} />
                      Order Status Timeline
                    </h3>
                    <span>(Using current status; full history can be added later)</span>
                  </div>
                  <ul className="timeline-list">
                    {['assigned', 'accepted', 'picked_up', 'in_transit', 'delivered'].map(
                      (step) => {
                        const isReached =
                          selectedItem.delivery_status === step ||
                          [
                            'accepted',
                            'picked_up',
                            'in_transit',
                            'delivered'
                          ].slice(
                            ['assigned', 'accepted', 'picked_up', 'in_transit', 'delivered'].indexOf(
                              step
                            )
                          ).includes(selectedItem.delivery_status);
                        return (
                          <li
                            key={step}
                            className={`timeline-item ${isReached ? 'reached' : ''}`}
                          >
                            <span className="dot" />
                            <span className="label">{step.replace('_', ' ')}</span>
                          </li>
                        );
                      }
                    )}
                  </ul>
                </div>

                <div className="tracking-footer-note">
                  Partner location history and detailed status events can be extended
                  using the <code>delivery_tracking</code> table.
                </div>
              </>
            ) : (
              <div className="tracking-details-empty">
                <Navigation size={40} />
                <h3>Select a delivery to view details</h3>
                <p>Pick an active order from the list to see its live tracking and details.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDeliveryTracking;


